import { lineItemTestID } from 'components/MerchantTrackingWidget/constants';
import {
  ItemContent,
  ItemDescription,
  ParcelContentBody,
  ThumbnailWrapper,
} from './SingleCard.styles';
import {
  DescriptionHyperLink,
  DescriptionRow,
  useFormatItemsCard,
  DescriptionTitle,
  DescriptionValue,
  TruncatedTitle,
  Thumbnail,
} from '../ItemCommons';
import { Card } from '../../../models';

interface SingleCardProps {
  card: Card;
}

const SingleCard = ({ card }: SingleCardProps): JSX.Element => {
  const {
    productName,
    productDescription,
    productUrl,
    productImageUrl,
    productPrice,
    productQuantity,
    productReasonForReturn,
    productCustomReasonForReturn,
    additionalInformation,
    productID,
  } = useFormatItemsCard(card.data);

  return (
    <ParcelContentBody>
      {productImageUrl && (
        <ThumbnailWrapper>
          <Thumbnail
            height={216}
            width={239}
            isRounded={false}
            src={productImageUrl}
            alt={productName}
          />
        </ThumbnailWrapper>
      )}
      <ItemContent>
        <ItemDescription>
          <DescriptionRow
            bottomMargin={12}
            data-testid={`${lineItemTestID}-name`}
          >
            <TruncatedTitle isShowFull>{productName}</TruncatedTitle>
          </DescriptionRow>

          {productPrice && (
            <DescriptionRow
              gap={4}
              bottomMargin={12}
              data-testid={`${lineItemTestID}-price`}
            >
              <DescriptionValue>{`${productPrice.label}: ${productPrice.value}`}</DescriptionValue>
            </DescriptionRow>
          )}
          {productQuantity && (
            <DescriptionRow
              gap={4}
              bottomMargin={12}
              data-testid={`${lineItemTestID}-quantity`}
            >
              <DescriptionValue>{productQuantity.value}</DescriptionValue>
            </DescriptionRow>
          )}
          {productID && (
            <DescriptionRow
              bottomMargin={12}
              data-testid={`${lineItemTestID}-id`}
            >
              <DescriptionValue>{`${productID.label}: ${productID.value}`}</DescriptionValue>
            </DescriptionRow>
          )}
          {additionalInformation && (
            <DescriptionRow
              gap={4}
              bottomMargin={12}
              data-testid={`${lineItemTestID}-additional-information`}
            >
              <DescriptionValue>{additionalInformation}</DescriptionValue>
            </DescriptionRow>
          )}
          {productReasonForReturn && (
            <DescriptionRow
              gap={4}
              bottomMargin={12}
              data-testid={`${lineItemTestID}-reason-for-return`}
            >
              <DescriptionTitle>
                {productReasonForReturn.label}:&nbsp;
                <DescriptionValue isInline>
                  {productReasonForReturn.value}
                </DescriptionValue>
              </DescriptionTitle>
            </DescriptionRow>
          )}
          {productCustomReasonForReturn && (
            <DescriptionRow
              gap={4}
              bottomMargin={12}
              data-testid={`${lineItemTestID}-custom-reason-for-return`}
            >
              <DescriptionTitle>
                {productCustomReasonForReturn.label}:&nbsp;
                <DescriptionValue isInline>
                  {productCustomReasonForReturn.value}
                </DescriptionValue>
              </DescriptionTitle>
            </DescriptionRow>
          )}
          {productDescription && (
            <DescriptionRow
              gap={4}
              bottomMargin={12}
              data-testid={`${lineItemTestID}-description`}
            >
              <DescriptionTitle>
                {productDescription.label}:&nbsp;
                <DescriptionValue isInline>
                  {productDescription.value}
                </DescriptionValue>
              </DescriptionTitle>
            </DescriptionRow>
          )}
        </ItemDescription>
        {productUrl && (
          <DescriptionRow
            gap={4}
            bottomMargin={0}
            data-testid={`${lineItemTestID}-url`}
          >
            <DescriptionHyperLink
              href={productUrl.value}
              target="_blank"
              aria-label={`${productUrl.label} ${productName}`}
            >
              {productUrl.label}
            </DescriptionHyperLink>
          </DescriptionRow>
        )}
      </ItemContent>
    </ParcelContentBody>
  );
};

export { SingleCard };
